import {
  HHButton,
  HHError,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { DataGridPro, GridPaginationModel } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { useDynamicDialogContext } from '../../context/dynamic-dialog.context';
import { useDynamicDrawerContext } from '../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../context/snackbar.context';
import { useBillingWriteAccess } from '../../hooks/use-billing-write-access';
import {
  IcdCode,
  useDeleteIcdCodeMutation,
  useUpdateIcdCodeMutation,
} from '../../types';
import { NoRowsOverlay } from '../grid-components/no-rows-or-error-overlay';
import { IcdCodeForm } from './icd-code-form/form';
import { IcdCodeFormValues } from './icd-code-form/validation-schema';
import { useIcdCodeColumns } from './use-icd-code-columns';
import { useIcdCodeQueries } from './use-icd-code-queries';

export const ICD_CODE_GRID_TEST_ID = 'icd-code-grid';

const DEFAULT_PAGE_SIZE = 50;

export const IcdCodeGrid = (): JSX.Element => {
  const canWrite = useBillingWriteAccess();
  const { rows, loading, hasError } = useIcdCodeQueries();
  const { setDrawerContent, onClose } = useDynamicDrawerContext();
  const { setDialogContent, closeDialog } = useDynamicDialogContext();
  const { setSnackbarState } = useSnackbarContext();

  const [updateIcdCode] = useUpdateIcdCodeMutation();
  const [deleteIcdCode] = useDeleteIcdCodeMutation();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const handleEdit = useCallback(
    (row: IcdCode): void => {
      const initialValues: IcdCodeFormValues = {
        indication: row.indication,
        universalCode: row.universalCode,
        rightCode: row.rightCode ?? '',
        leftCode: row.leftCode ?? '',
        startDate: dayjs(row.startDate).toDate(),
        endDate: dayjs(row.endDate).toDate(),
      };

      const handleUpdate = async (values: IcdCodeFormValues): Promise<void> => {
        try {
          await updateIcdCode({
            variables: {
              id: row.id,
              icdCodeInput: {
                rightCode: values.rightCode || null,
                leftCode: values.leftCode || null,
                startDate: dayjs(values.startDate).format('YYYY-MM-DD'),
                endDate: dayjs(values.endDate).format('YYYY-MM-DD'),
              },
            },
            refetchQueries: ['getIcdCodes'],
          });
          setSnackbarState('ICD code updated successfully', 'success', 3000);
          onClose();
        } catch {
          setSnackbarState('Error updating ICD code', 'error', 3000);
        }
      };

      setDrawerContent(
        <HHStack padding={4}>
          <IcdCodeForm onSubmit={handleUpdate} initialValues={initialValues} />
        </HHStack>,
        <HHTypography hhVariant="h6">Edit ICD Code</HHTypography>,
        undefined,
        'temporary',
      );
    },
    [setDrawerContent, updateIcdCode, setSnackbarState, onClose],
  );

  const handleDelete = useCallback(
    (row: IcdCode): void => {
      const executeDelete = async (): Promise<void> => {
        closeDialog();
        try {
          await deleteIcdCode({
            variables: { id: row.id },
            refetchQueries: ['getIcdCodes'],
          });
          setSnackbarState('ICD code deleted successfully', 'success', 3000);
        } catch {
          setSnackbarState('Error deleting ICD code', 'error', 3000);
        }
      };

      setDialogContent({
        title: (
          <HHTypography hhVariant="section-title">Delete ICD Code</HHTypography>
        ),
        content: (
          <HHStack spacing={2}>
            <HHTypography hhVariant="body1">
              Are you sure you want to delete this ICD code ({row.universalCode}{' '}
              — {row.indication})?
            </HHTypography>
          </HHStack>
        ),
        footer: (
          <HHStack direction="row" spacing={2} padding={1}>
            <HHButton
              hhVariant="variant-bypass"
              variant="outlined"
              onClick={closeDialog}
              data-testid="icd-code-delete-cancel-button"
            >
              Cancel
            </HHButton>
            <HHButton
              hhVariant="variant-bypass"
              variant="contained"
              color="error"
              onClick={(): void => {
                executeDelete().catch(() => undefined);
              }}
              data-testid="icd-code-delete-confirm-button"
            >
              Delete
            </HHButton>
          </HHStack>
        ),
        width: 'sm',
        fullWidth: true,
      });
    },
    [setDialogContent, closeDialog, deleteIcdCode, setSnackbarState],
  );

  const columns = useIcdCodeColumns({
    onEdit: handleEdit,
    onDelete: handleDelete,
    canWrite,
  });

  if (hasError) {
    return (
      <HHError body="Error fetching ICD codes" hhVariant="variant-bypass" />
    );
  }

  return (
    <HHStack
      flex={1}
      direction="column"
      spacing={6}
      justifyContent="space-between"
      minHeight="0"
    >
      <DataGridPro
        columns={columns}
        rows={rows}
        loading={loading}
        getRowId={(row): number => row.id}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationMode="client"
        initialState={{
          sorting: { sortModel: [{ field: 'indication', sort: 'asc' }] },
        }}
        sortingMode="client"
        filterMode="client"
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        pinnedColumns={{ right: ['actions'] }}
        aria-label="ICD codes table"
        data-testid={ICD_CODE_GRID_TEST_ID}
        {...(process.env.NODE_ENV === 'test' && {
          disableVirtualization: true,
        })}
        slots={{
          noResultsOverlay: NoRowsOverlay,
          noRowsOverlay: NoRowsOverlay,
        }}
        slotProps={{
          noResultsOverlay: { error: hasError } as Record<string, boolean>,
          noRowsOverlay: { error: hasError } as Record<string, boolean>,
        }}
      />
    </HHStack>
  );
};
